<template>
  <v-dialog
    v-model="dialogModel"
    max-width="560"
  >
    <v-card v-if="transaction" tile outlined class="txn-dialog-card">
      <div class="txn-section">
        <div class="txn-section__header">
          <v-icon
            v-if="transaction.logTypeIconData"
            small
            color="primary"
          >{{ transaction.logTypeIconData.icon }}</v-icon>
          <span>{{ typeLabel }}</span>
          <span class="txn-section__header-id">{{ transaction.idLabel }}</span>
          <button
            type="button"
            class="txn-section__close"
            aria-label="Close"
            @click="closeDialog"
          >
            <v-icon>fas fa-times</v-icon>
          </button>
        </div>
        <div class="txn-section__body">
          <div class="txn-highlight txn-highlight--pair">
            <div class="txn-highlight__primary">
              <div :class="['txn-highlight__value', transaction.amount < 0 ? 'text--muted' : '']">
                {{ transaction.amountLabel }}
              </div>
            </div>
            <div class="txn-highlight__status">
              <div class="txn-highlight__status-value">
                <v-icon
                  v-if="transaction.logStatusIconData"
                  small
                  class="mr-1"
                  :color="transaction.logStatusIconData.color"
                >{{ transaction.logStatusIconData.icon }}</v-icon>
                {{ statusLabel }}
              </div>
            </div>
          </div>
          <div v-if="errorLabel" class="txn-error">{{ errorLabel }}</div>
          <div v-if="conversionRateLabel" class="txn-row">
            <span class="txn-row__label">Conversion rate</span>
            <span class="txn-row__value">{{ conversionRateLabel }}</span>
          </div>
          <div v-if="profitLabel" class="txn-row txn-row--profit">
            <span class="txn-row__label">
              <v-icon small class="mr-1" color="#2e7d32">fas fa-chart-line</v-icon>
              Profit
            </span>
            <span class="txn-row__value">{{ profitLabel }}</span>
          </div>
          <div v-if="customerTransactionIdLabel" class="txn-row">
            <span class="txn-row__label">Customer transaction ID</span>
            <span class="txn-row__value txn-row__value--id">{{ customerTransactionIdLabel }}</span>
          </div>
        </div>
      </div>

      <div v-if="hasAccount" class="txn-section">
        <div class="txn-section__header">
          <v-icon small color="primary">fas fa-mobile-alt</v-icon>
          <span>Account</span>
        </div>
        <div class="txn-section__body">
          <div v-if="transaction.passengerPhoneNumberLabel" class="txn-row">
            <span class="txn-row__label">Sent to</span>
            <span class="txn-row__value">{{ transaction.passengerPhoneNumberLabel }}</span>
          </div>
          <div v-if="transaction.phoneNumberLabel" class="txn-row">
            <span class="txn-row__label">Mobile money</span>
            <span class="txn-row__value">{{ transaction.phoneNumberLabel }}</span>
          </div>
        </div>
      </div>

      <div v-if="hasBalance" class="txn-section">
        <div class="txn-section__header">
          <v-icon small color="primary">fas fa-wallet</v-icon>
          <span>Balance</span>
        </div>
        <div class="txn-section__body">
          <div class="txn-row">
            <span class="txn-row__label">Before</span>
            <span class="txn-row__value">{{ transaction.profileBalanceBeforeLabel || '—' }}</span>
          </div>
          <div class="txn-row">
            <span class="txn-row__label">After</span>
            <span class="txn-row__value">{{ transaction.profileBalanceAfterLabel || '—' }}</span>
          </div>
        </div>
      </div>

      <div class="txn-section">
        <div class="txn-section__header">
          <v-icon small color="primary">far fa-clock</v-icon>
          <span>Dates</span>
        </div>
        <div class="txn-section__body">
          <div class="txn-row">
            <span class="txn-row__label">Created</span>
            <span class="txn-row__value">{{ transaction.dateCreatedLabel }}</span>
          </div>
          <div class="txn-row">
            <span class="txn-row__label">Updated</span>
            <span class="txn-row__value">{{ transaction.dateUpdatedLabel }}</span>
          </div>
        </div>
      </div>

      <div class="txn-section txn-section--metadata">
        <div class="txn-section__header">
          <v-icon small color="primary">fas fa-code</v-icon>
          <span>Metadata</span>
        </div>
        <div class="txn-section__body txn-section__body--metadata">
          <json-viewer
            :value="logMetadataValue"
            :expand-depth="1"
            boxed
            copyable
            sort
            class="txn-json-viewer"
          />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.txn-dialog-card {
  max-height: 85vh;
  overflow-y: auto;
}

.txn-section {
  overflow: hidden;
}

.txn-section--metadata {
  overflow: visible;
}

.txn-section__body--metadata {
  padding: 8px;
}

.txn-json-viewer {
  font-size: 12px;
}

::v-deep .txn-json-viewer .jv-code {
  padding: 12px;
}

.txn-section + .txn-section {
  border-top: 1px solid #e6eaef;
}

.txn-section__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f4f6f8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #3d4654;
}

.txn-section__header-id {
  margin-left: auto;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #666;
}

.txn-section__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  margin: -6px -8px -6px 8px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #666;
}

.txn-section__body {
  padding: 8px 12px 10px;
}

.txn-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0 2px;
  font-size: 13px;
  line-height: 1.35;
}

.txn-row:first-child {
  padding-top: 0;
}

.txn-row__label {
  color: #666;
  flex: 0 1 auto;
}

.txn-row__value {
  font-weight: 600;
  text-align: right;
  min-width: 0;
}

.txn-row__value--id {
  letter-spacing: 0.04em;
}

.txn-row--profit {
  align-items: center;
}

.txn-row--profit .txn-row__label,
.txn-row--profit .txn-row__value,
.txn-row--profit ::v-deep .v-icon {
  color: #2e7d32;
}

.txn-row--profit .txn-row__label {
  display: inline-flex;
  align-items: center;
}

.txn-error {
  max-height: 7.5rem;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.4;
  color: #c43e18;
  margin-top: 8px;
}

.txn-highlight {
  background: #fafbfc;
  border-left: 3px solid #204da2;
  border-radius: 2px;
  padding: 8px 10px;
}

.txn-highlight--pair {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.txn-highlight__primary {
  min-width: 0;
}

.txn-highlight__value {
  font-size: 1.35em;
  font-weight: 700;
  line-height: 1.25;
}

.txn-highlight__status {
  flex: 0 0 auto;
  text-align: right;
}

.txn-highlight__status-value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 1.05em;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
}
</style>

<script>
import src from './src'
export default src
</script>
